import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createCommentVNode as _createCommentVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createBlock as _createBlock, renderSlot as _renderSlot, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id", "role"]
const _hoisted_2 = {
  key: 0,
  class: "dialog-header-start q-mr-xs"
}
const _hoisted_3 = { class: "full-width q-pl-xs dialog-header-content" }
const _hoisted_4 = {
  key: 0,
  class: "text-caption items-center row"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 1,
  class: "dialog-header-end"
}
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { key: 0 }

import { toRefs, ref, watch, onBeforeUnmount, nextTick, computed } from 'vue';
import { QDialog, useQuasar } from 'quasar';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

// Stores
import { useRouterStore } from '@stores/router';

// Personal components
import QBtnClose from '@components/form/QBtnClose.vue';
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import DiagGeneric from '@components/dialog/DiagGeneric.vue';
import QNotificationIcon from '@components/notification/QNotificationIcon.vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal libs
import bus from '@shared/lib/eventbus.lib';
import { BusEventDialogEnum } from '@shared/enum/bus/bus-events-dialog.enum';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import { NotificationTypeEnum } from '@shared/enum/notification.enum';
import { IQDialogButton } from '@shared/interface/qDialogTemplate.interface';

type QDialogPosition =
  | 'bottom'
  | 'left'
  | 'right'
  | 'top'
  | 'standard'
  | undefined;

type QDialogTransition = 'slide' | 'fade'

/**
 * Events
 */
export interface QDialogTemplateProps {
  showDialog?: boolean;
  persistent?: boolean;
  maximized?: boolean;
  loadingFirst?: boolean;
  loadingSecond?: boolean;
  title?: string;
  subtitle?: string;
  subtitleHtml?: string;
  message?: string;
  isHtml?: boolean;
  displayCloseButton?: boolean;
  disableCloseButton?: boolean;
  dialogSize?: DialogSizeEnum;
  notificationType?: NotificationTypeEnum;
  isForm?: boolean;
  okBtn?: IQDialogButton;
  cancelBtn?: IQDialogButton;
  position?: QDialogPosition;
  seamless?: boolean;
  height?: number;
  transition?: QDialogTransition;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'QDialogTemplate',
  props: {
    showDialog: { type: Boolean, default: false },
    persistent: { type: Boolean, default: false },
    maximized: { type: Boolean, default: false },
    loadingFirst: { type: Boolean, default: false },
    loadingSecond: { type: Boolean, default: false },
    title: { default: '' },
    subtitle: {},
    subtitleHtml: {},
    message: {},
    isHtml: { type: Boolean },
    displayCloseButton: { type: Boolean, default: true },
    disableCloseButton: { type: Boolean, default: false },
    dialogSize: { default: DialogSizeEnum.SMALL },
    notificationType: {},
    isForm: { type: Boolean, default: false },
    okBtn: {},
    cancelBtn: {},
    position: {},
    seamless: { type: Boolean, default: false },
    height: {},
    transition: {}
  },
  emits: ['close'],
  setup(__props: any, { emit: __emit }) {

// Vendor
const emits = __emit;

/**
 * HTML Elements
 */
const dialogRefInside = ref<HTMLElement>();

/**
 * Globals
 */
const $q = useQuasar();

/**
 * Stores
 */
const routerStore = useRouterStore();

/**
 * Viewmodel
 */
const { isMobile, vmOnClose } = useGlobalVM();

/**
 * Properties
 */
const props = __props;
const {
  showDialog,
  persistent,
  maximized,
  loadingFirst,
  loadingSecond,
  title,
  message,
  isHtml,
  subtitle,
  subtitleHtml,
  displayCloseButton,
  disableCloseButton,
  dialogSize,
  notificationType,
  isForm,
  okBtn,
  cancelBtn,
  position,
  seamless,
  height,
  transition
} = toRefs(props);

/**
 * Reactive datas
 */
const newShow = ref<boolean | undefined>(false);
const showCloseConfirm = ref<boolean | undefined>(false);
const qDialogId = ref<string>('');

/**
 * Computed properties
 */
const computedTransitionShow = computed<string>(() => {
  if (transition.value != null) {
    if (transition.value === 'slide') {
      return 'slide-up'
    } else if (transition.value === 'fade') {
      return 'fade'
    } else {
      return 'fade'
    }
  } else {
      return isMobile || position.value === 'bottom' ? 'slide-up' : 'fade'
  }
})
const computedTransitionHide = computed<string>(() => {
  if (transition.value != null) {
    if (transition.value === 'slide') {
      return 'slide-down'
    } else if (transition.value === 'fade') {
      return 'fade'
    } else {
      return 'fade'
    }
  } else {
      return isMobile || position.value === 'bottom' ? 'slide-down' : 'fade'
  }
})
const computedTitle = computed<string>(() => {
  if (title.value != null && title.value != '') {
    return title.value;
  } else if (notificationType.value == NotificationTypeEnum.ERROR) {
    return 'Erreur';
  } else if (notificationType.value == NotificationTypeEnum.SUCCESS) {
    return 'Succès';
  } else if (notificationType.value == NotificationTypeEnum.WARNING) {
    return 'Attention';
  } else {
    return 'Info';
  }
});

const notificationRole = computed<string | undefined>(() => {
  if (notificationType.value != null) {
    if (
      notificationType.value == NotificationTypeEnum.ERROR ||
      notificationType.value == NotificationTypeEnum.WARNING
    ) {
      return 'alert';
    } else if (notificationType.value == NotificationTypeEnum.SUCCESS) {
      return 'status';
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
});

const dialogSecondaryRole = computed<string | undefined>(() =>
  notificationRole.value ?? isForm.value ? 'form' : undefined,
);

/**
 * Focus trapping
 */
const { activate, deactivate } = useFocusTrap(dialogRefInside, {
  // delayInitialFocus: true,
  immediate: true,
  // allowOutsideClick: (e: MouseEvent | TouchEvent) => {
  //   logger?.info(e);
  //   return true;
  // },
  allowOutsideClick: true,
});

/**
 * Dialog events handler
 */
function onCloseDialogGeneric() {
  activate();
  showCloseConfirm.value = false;
}

function onClose(uuidToClose?: string) {
  vmOnClose(uuidToClose ?? qDialogId.value);

  emits('close');
}

function onHide() {
  if (cancelBtn.value?.handler != null) {
    cancelBtn.value.handler();
  }

  onClose();
}

function onBeforeHide() {
  clearFocusTrap();
}

function onBeforeShow() {
  qDialogId.value = crypto.randomUUID();
}

function onShow() {
  initFocusTrap();
}

function onShake() {
  if (persistent.value) showCloseConfirm.value = true;
}

function onCancelClick() {
  if (cancelBtn.value?.handler != null) {
    cancelBtn.value.handler();
  }

  vmOnClose(qDialogId.value);

  newShow.value = false;
}

function onOkClick() {
  if (okBtn.value?.handler != null) {
    okBtn.value.handler();
  }

  vmOnClose(qDialogId.value);

  newShow.value = false;
}

/**
 * Focus trap functions
 */
async function initFocusTrap() {
  await nextTick();

  activate();
}

function clearFocusTrap() {
  deactivate();
}

/**
 * Watch
 */
watch(
  () => showDialog.value,
  (newVal: boolean | undefined, oldVal: boolean | undefined) => {
    if (!oldVal && newVal === true) {
      bus.on(BusEventDialogEnum.CLOSE_LAST_MODAL, () => {
        const uuidToClose = routerStore.getLastInserted();

        nextTick().then(() => {
          if (
            loadingFirst.value !== true &&
            loadingSecond.value !== true &&
            qDialogId.value === uuidToClose
          ) {
            routerStore.stopRouterNavigation = false;
            onClose(uuidToClose);
          }
        });
      });

      bus.on(BusEventDialogEnum.CLOSE_ALL_MODAL, () => {
        const uuidsToClose = routerStore.modalsOpened;

        nextTick().then(() => {
          const dialogFound = uuidsToClose.find((u) => qDialogId.value);
          if (
            loadingFirst.value !== true &&
            loadingSecond.value !== true &&
            dialogFound != null
          ) {
            routerStore.stopRouterNavigation = false;
            vmOnClose(dialogFound);
            onClose(dialogFound);
          }
        });
      });

      nextTick().then(() => {
        showCloseConfirm.value = false;

        // Manage back button : add opened modal to stack
        qDialogId.value = crypto.randomUUID();
        routerStore.addOpenedModal(qDialogId.value);
      });
    }

    newShow.value = newVal;
  },
  { immediate: true },
);

/**
 * Hooks
 */
onBeforeUnmount(() => {
  if (routerStore.modalsOpened.length === 0) {
    bus.off(BusEventDialogEnum.CLOSE_LAST_MODAL);
    bus.off(BusEventDialogEnum.CLOSE_ALL_MODAL);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(_unref(QDialog), {
    "aria-labelledby": "dialog_label_id",
    modelValue: newShow.value,
    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((newShow).value = $event)),
    persistent: _unref(persistent),
    maximized: _unref(maximized),
    position: _unref(position),
    seamless: _unref(seamless),
    "transition-show": computedTransitionShow.value,
    "transition-hide": computedTransitionHide.value,
    onClose: _cache[7] || (_cache[7] = ($event: any) => (onClose())),
    onHide: _cache[8] || (_cache[8] = ($event: any) => (onHide())),
    onBeforeHide: _cache[9] || (_cache[9] = ($event: any) => (onBeforeHide())),
    onShow: _cache[10] || (_cache[10] = ($event: any) => (onShow())),
    onBeforeShow: _cache[11] || (_cache[11] = ($event: any) => (onBeforeShow())),
    onShake: _cache[12] || (_cache[12] = ($event: any) => (onShake())),
    ref: "dialogRefContainer"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "dialogRefInside",
        ref: dialogRefInside,
        id: qDialogId.value,
        role: dialogSecondaryRole.value,
        class: _normalizeClass([
        'dialog-container',
        'q-py-none',
        {
          'dialog-container-desktop-height': !_unref(isMobile),
          'dialog-container-desktop-width': !_unref(isMobile),
          'dialog-extra-small':
            _unref(dialogSize) == _unref(DialogSizeEnum).EXTRA_SMALL && !_unref(maximized),
          'dialog-small': _unref(dialogSize) == _unref(DialogSizeEnum).SMALL && !_unref(maximized),
          'dialog-large': _unref(dialogSize) == _unref(DialogSizeEnum).LARGE && !_unref(maximized),
          'dialog-auto': _unref(dialogSize) == _unref(DialogSizeEnum).AUTO,
          'bg-white': !_unref($q).dark.isActive,
          'q-dark': _unref($q).dark.isActive,
        },
      ])
      }, [
        _createCommentVNode(" HEADER "),
        _createVNode(_component_q_card_section, {
          class: _normalizeClass([
          'no-scroll full-width text-white dialog-header',
          _unref(notificationType) != null
            ? `notification-${_unref(notificationType)}`
            : 'bg-primary',
        ])
        }, {
          default: _withCtx(() => [
            _createCommentVNode(" HEADER - start "),
            (_unref(displayCloseButton))
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(QBtnClose, {
                    disable: _unref(disableCloseButton) || _unref(loadingFirst) || _unref(loadingSecond),
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (onClose()))
                  }, null, 8 /* PROPS */, ["disable"])
                ]))
              : _createCommentVNode("v-if", true),
            _createCommentVNode(" HEADER - content "),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("h2", {
                id: "dialog_label_id",
                class: _normalizeClass([
              'text-h7 items-center',
              { row: _unref(subtitle) != '' || _unref(subtitleHtml) != '' },
            ])
              }, _toDisplayString(computedTitle.value), 3 /* TEXT, CLASS */),
              (_unref(subtitle) != '' || _unref(subtitleHtml) != '')
                ? (_openBlock(), _createElementBlock("h3", _hoisted_4, [
                    (_unref(subtitle) != '')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(subtitle)), 1 /* TEXT */))
                      : _createCommentVNode("v-if", true),
                    (_unref(subtitleHtml) != '')
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          innerHTML: _unref(subtitleHtml)
                        }, null, 8 /* PROPS */, _hoisted_6))
                      : _createCommentVNode("v-if", true)
                  ]))
                : _createCommentVNode("v-if", true)
            ]),
            _createCommentVNode(" HEADER - end "),
            (_ctx.$slots.header)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  (_unref(notificationType) != null)
                    ? (_openBlock(), _createBlock(QNotificationIcon, {
                        key: 0,
                        notificationType: _unref(notificationType)
                      }, null, 8 /* PROPS */, ["notificationType"]))
                    : _createCommentVNode("v-if", true),
                  (_ctx.$slots.header)
                    ? _renderSlot(_ctx.$slots, "header", { key: 1 })
                    : _createCommentVNode("v-if", true)
                ]))
              : _createCommentVNode("v-if", true)
          ]),
          _: 3 /* FORWARDED */
        }, 8 /* PROPS */, ["class"]),
        _createCommentVNode(" CONTENT "),
        _createElementVNode("div", {
          class: "dialog-content scroll",
          style: _normalizeStyle(_unref(height) ? `height:${_unref(height)}px!important` : undefined)
        }, [
          (_unref(message))
            ? (_openBlock(), _createBlock(_component_q_card_section, { key: 0 }, {
                default: _withCtx(() => [
                  (_unref(isHtml))
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        innerHTML: _unref(message)
                      }, null, 8 /* PROPS */, _hoisted_8))
                    : (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_unref(message)), 1 /* TEXT */))
                ]),
                _: 1 /* STABLE */
              }))
            : _createCommentVNode("v-if", true),
          (_ctx.$slots.content)
            ? _renderSlot(_ctx.$slots, "content", { key: 1 })
            : _createCommentVNode("v-if", true)
        ], 4 /* STYLE */),
        _createCommentVNode(" FOOTER "),
        (_ctx.$slots.footer || _unref(okBtn) != null)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _renderSlot(_ctx.$slots, "footer", {}, () => [
                _createVNode(_component_q_separator),
                _createVNode(_component_q_card_actions, { align: "right" }, {
                  default: _withCtx(() => [
                    (_unref(cancelBtn))
                      ? (_openBlock(), _createBlock(QBtnCancel, {
                          key: 0,
                          label: _unref(cancelBtn).label ? _unref(cancelBtn).label : 'Annuler',
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (onCancelClick())),
                          disable: _unref(loadingFirst)
                        }, null, 8 /* PROPS */, ["label", "disable"]))
                      : _createCommentVNode("v-if", true),
                    (_unref(okBtn))
                      ? (_openBlock(), _createBlock(QBtnValid, {
                          key: 1,
                          label: _unref(okBtn).label ? _unref(okBtn).label : 'Valider',
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (onOkClick())),
                          disable: _unref(loadingFirst),
                          loading: _unref(loadingFirst),
                          "loading-label": '',
                          "notification-type": _unref(notificationType)
                        }, null, 8 /* PROPS */, ["label", "disable", "loading", "notification-type"]))
                      : _createCommentVNode("v-if", true)
                  ]),
                  _: 1 /* STABLE */
                })
              ])
            ]))
          : _createCommentVNode("v-if", true)
      ], 10 /* CLASS, PROPS */, _hoisted_1),
      _createVNode(DiagGeneric, {
        show: showCloseConfirm.value,
        title: 'Fermer la fenêtre',
        message: '<p>Voulez-vous abandonner et fermer la fenêtre actuellement ouverte ?</p><p>Nota bene : en faisant cela, vous perdrez le focus sur l\'élement qui a ouvert la fenêtre précédente.</p>',
        persistent: false,
        html: true,
        ok: { label: 'Fermer' },
        cancel: { label: 'Annuler' },
        "dialog-size": _unref(DialogSizeEnum).EXTRA_SMALL,
        onCancel: _cache[3] || (_cache[3] = ($event: any) => (onCloseDialogGeneric())),
        onClose: _cache[4] || (_cache[4] = ($event: any) => (onCloseDialogGeneric())),
        onValid: _cache[5] || (_cache[5] = ($event: any) => (onClose()))
      }, null, 8 /* PROPS */, ["show", "dialog-size"])
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["modelValue", "persistent", "maximized", "position", "seamless", "transition-show", "transition-hide"]))
}
}

})